import { useEffect, useState } from "react";

import { Loader2, Shield } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAssignment, useSetAssignment } from "@/features/rbac/hooks/useAssignments";
import { useRoles } from "@/features/rbac/hooks/useRoles";
import type { SubjectType } from "@/features/rbac/schemas/rbac";

interface AssignRolesDialogProps {
  subject: {
    uid: string;
    email: string | null;
    displayName: string;
    /** Permisos heredados, para explicar de dónde salen los roles actuales. */
    legacyPermissions?: readonly string[];
    subjectType?: SubjectType;
  } | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Asigna roles a una persona.
 *
 * Una lista vacía borra la asignación, con lo que la persona vuelve a resolver
 * sus permisos por la vía heredada en vez de quedarse sin nada.
 */
export function AssignRolesDialog({ subject, open, onClose }: AssignRolesDialogProps) {
  const { data: roles = [], isLoading: rolesLoading } = useRoles();
  const { data: assignment, isLoading: assignmentLoading } = useAssignment(
    open ? (subject?.uid ?? null) : null
  );
  const setAssignment = useSetAssignment();

  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setSelected(
      assignment?.roleIds.length
        ? [...assignment.roleIds]
        : [...(subject?.legacyPermissions ?? [])]
    );
  }, [open, assignment, subject]);

  const isLoading = rolesLoading || assignmentLoading;
  const usesLegacy = !assignment || assignment.roleIds.length === 0;

  const toggle = (roleId: string) => {
    setSelected((previous) =>
      previous.includes(roleId)
        ? previous.filter((id) => id !== roleId)
        : [...previous, roleId]
    );
  };

  const handleSave = async () => {
    if (!subject) return;

    try {
      await setAssignment.mutateAsync({
        subjectUid: subject.uid,
        subjectType: subject.subjectType ?? "user",
        subjectEmail: subject.email,
        roleIds: selected,
      });
      toast.success(`Roles actualizados para ${subject.displayName}`);
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudieron guardar los roles"
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Roles de {subject?.displayName ?? "la persona"}</DialogTitle>
          <DialogDescription>
            {subject?.email}
            {usesLegacy && (
              <span className="mt-2 block">
                Todavía resuelve sus permisos por la vía heredada. Al guardar se creará
                una asignación explícita.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
            {roles.map((role) => (
              <label
                key={role.id}
                className="flex items-start gap-3 rounded-lg border p-3 hover:bg-muted/50"
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={selected.includes(role.id)}
                  onChange={() => toggle(role.id)}
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{role.name}</span>
                    {role.system && (
                      <Badge variant="secondary" className="gap-1">
                        <Shield className="h-3 w-3" />
                        Sistema
                      </Badge>
                    )}
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    {role.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Los permisos de varios roles se suman. Si no marcas ninguno, la persona volverá
          a resolver por sus permisos heredados.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={setAssignment.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isLoading || setAssignment.isPending}
          >
            {setAssignment.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar roles
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
